import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, ClipboardCheck, RotateCcw, Search, ShieldCheck } from "lucide-react";
import { AttendanceCarousel } from "@/components/mantab/AttendanceCarousel";
import { AttendanceScheduleCard } from "@/components/mantab/AttendanceScheduleCard";
import { AttendanceSuccessPage } from "@/components/mantab/AttendanceSuccessPage";
import "@/pages/mantab/MantabAttendance.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MantabStudentAttendance() {
  const [identifier, setIdentifier] = useState("");
  const [lookup, setLookup] = useState(null);
  const [checked, setChecked] = useState({});
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);

  const runLookup = useCallback(async (silent = false) => {
    const value = identifier.trim();
    if (value.replace(/[^A-Za-z0-9]/g, "").length < 4) { setError("Masukkan NIK atau Nomor Pendaftaran yang valid."); return; }
    if (!silent) { setLoading(true); setError(""); setLookup(null); setChecked({}); setReady(false); }
    try {
      const response = await axios.get(`${API}/mantab/public/attendance/lookup`, { params: { identifier: value } });
      setLookup(response.data);
      if (response.data.result !== "scheduled") setError(response.data.message);
    } catch (requestError) {
      if (!silent) setError(requestError.response?.data?.detail || "Data belum dapat diperiksa. Silakan coba lagi.");
    } finally { if (!silent) setLoading(false); }
  }, [identifier]);

  useEffect(() => {
    if (lookup?.result !== "scheduled" || lookup?.window?.status === "closed" || receipt) return undefined;
    const timer = window.setInterval(() => runLookup(true), 30_000);
    return () => window.clearInterval(timer);
  }, [lookup?.result, lookup?.window?.status, receipt, runLookup]);
  useEffect(() => {
    if (receipt) window.scrollTo(0, 0);
  }, [receipt]);

  const submitAttendance = async () => {
    setSubmitting(true); setError("");
    try {
      const response = await axios.post(`${API}/mantab/public/attendance/submit`, {
        identifier: identifier.trim(), confirmations: Object.keys(checked).filter((id) => checked[id]),
      });
      setReceipt(response.data);
    } catch (requestError) { setError(requestError.response?.data?.detail || "Daftar hadir belum dapat dicatat. Silakan coba lagi."); }
    finally { setSubmitting(false); }
  };

  const resetSearch = () => { setIdentifier(""); setLookup(null); setChecked({}); setReady(false); setError(""); setReceipt(null); };
  const scheduled = lookup?.result === "scheduled";
  const windowOpen = lookup?.window?.status === "open";
  const windowClosed = lookup?.window?.status === "closed";

  if (receipt) return <AttendanceSuccessPage receipt={receipt} onBack={resetSearch} />;

  return <div className="attendance-page" data-testid="student-attendance-page">
    <header className="attendance-public-header">
      <div><img src="/logo-padma-transparent.png" alt="Logo PADMA" /><span><strong>MANTAB</strong><small data-testid="attendance-program-name">Manajemen Buku Tabungan bagi Mahasiswa KIPK Baru</small></span></div>
      <em><ShieldCheck size={15} /> LLDIKTI WILAYAH VII</em>
    </header>
    <main className="attendance-main">
      <section className="attendance-intro">
        <div className="attendance-kicker"><ClipboardCheck size={16} /> KONFIRMASI KEHADIRAN</div>
        <h1 data-testid="attendance-page-title">Daftar Hadir Distribusi Buku Tabungan<span>KIPK LLDIKTI Wilayah VII</span></h1>
        <p>Periksa jadwal Anda, baca seluruh informasi, lalu konfirmasikan kehadiran saat sudah berada di lokasi.</p>
      </section>
      <form className="attendance-lookup" onSubmit={(event) => { event.preventDefault(); runLookup(); }} data-testid="attendance-lookup-form">
        <label htmlFor="attendance-identifier">Masukkan NIK atau Nomor Pendaftaran</label>
        <div><Search size={18} /><input id="attendance-identifier" value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder="Contoh: 351508... / 01.1234.5678" autoComplete="off" inputMode="text" data-testid="attendance-lookup-input" /><button type="submit" disabled={loading} data-testid="attendance-lookup-button">{loading ? "Memeriksa…" : "Periksa Jadwal"}</button></div>
        <small>Tanda titik, spasi, atau garis pada Nomor Pendaftaran boleh disertakan.</small>
      </form>
      {error && <div className="attendance-alert error" role="alert" data-testid="attendance-error-alert">{error}</div>}
      {scheduled && <>
        {windowClosed ? <div className="attendance-closed-notice" role="status" data-testid="attendance-closed-notice"><span><strong>KEGIATAN SUDAH SELESAI</strong><small>{lookup.window.message}</small></span><button type="button" onClick={resetSearch} data-testid="attendance-reset-search"><RotateCcw size={14} /> Ganti nomor</button></div> : <div className="attendance-found-note" data-testid="attendance-found-notice">{lookup.message}<button type="button" onClick={resetSearch} data-testid="attendance-reset-search"><RotateCcw size={14} /> Ganti nomor</button></div>}
        <AttendanceScheduleCard student={lookup.student} schedule={lookup.schedule} />
        {!windowClosed && <AttendanceCarousel key={lookup.schedule.schedule_id} checked={checked} setChecked={setChecked} onReadinessChange={setReady} requireConfirmation={windowOpen}>
          <div className={`attendance-window ${windowOpen ? "open" : "closed"}`} data-testid="attendance-window-notice">
            <strong>{windowOpen ? "SILAHKAN KLIK DAFTAR HADIR" : lookup.window.message}</strong>
          </div>
          {windowOpen && ready && <button type="button" className="attendance-submit" onClick={submitAttendance} disabled={submitting} data-testid="attendance-submit-button"><CheckCircle2 size={19} /> {submitting ? "Mencatat Kehadiran…" : "DAFTAR HADIR SEKARANG"}</button>}
        </AttendanceCarousel>}
      </>}
    </main>
    <footer className="attendance-footer"><span>MANTAB · PADMA</span><em>Manajemen Buku Tabungan bagi Mahasiswa KIPK Baru</em></footer>
  </div>;
}